import * as m from '@staiter/i18n';
import type { JSX } from 'react';
import type { DroppedAssignee, TemplateSkipped } from '~/lib/tasks/api/blueprints';
import styles from './TemplateOpSummary.module.css';

/**
 * TemplateOpSummary — единая сводка «что не вошло» (I5) для трёх диалогов
 * (материализация / сохранение / дублирование). BE шлёт только enum-коды +
 * counts; карта «код → i18n» живёт ЗДЕСЬ (единственная копия). Рендерит только
 * ненулевые категории; неизвестный код не роняет рендер.
 */

// Единственная карта skip-код → локализованная строка ({count}).
// Экспортируется для страницы просмотра шаблона (F2): вторая копия карты
// разъехалась бы с этой при добавлении кода в `TEMPLATE_SKIP_CODES`.
export const SKIP_LABEL: Record<string, (p: { count: number }) => string> = {
  external_deps: m.tmpl_skip_external_deps,
  secondary_tasks: m.tmpl_skip_secondary_tasks,
  done_tasks: m.tmpl_skip_done_tasks,
  recurring_instances: m.tmpl_skip_recurring_instances,
  attachments: m.tmpl_skip_attachments,
  comments: m.tmpl_skip_comments,
  worklogs: m.tmpl_skip_worklogs,
  archived_tasks: m.tmpl_skip_archived_tasks,
  date_custom_fields: m.tmpl_skip_date_custom_fields,
  orphan_custom_field_keys: m.tmpl_skip_orphan_custom_field_keys,
  missing_entity_labels: m.tmpl_skip_missing_entity_labels,
};

/**
 * Те же коды, но БЕЗ вшитого счётчика — для сводки, где число живёт в своей
 * колонке (просмотр шаблона, F-B6). Держим рядом с `SKIP_LABEL`: разъехаться
 * двум картам одного enum'а тут проще всего.
 */
export const SKIP_LABEL_TEXT: Record<string, () => string> = {
  external_deps: m.tmpl_skip_label_external_deps,
  secondary_tasks: m.tmpl_skip_label_secondary_tasks,
  done_tasks: m.tmpl_skip_label_done_tasks,
  recurring_instances: m.tmpl_skip_label_recurring_instances,
  attachments: m.tmpl_skip_label_attachments,
  comments: m.tmpl_skip_label_comments,
  worklogs: m.tmpl_skip_label_worklogs,
  archived_tasks: m.tmpl_skip_label_archived_tasks,
  date_custom_fields: m.tmpl_skip_label_date_custom_fields,
  orphan_custom_field_keys: m.tmpl_skip_label_orphan_custom_field_keys,
  missing_entity_labels: m.tmpl_skip_label_missing_entity_labels,
};

export type TemplateOpSummaryProps = {
  skipped?: TemplateSkipped | undefined;
  warnings?: string[] | undefined;
  droppedAssignees?: DroppedAssignee[] | undefined;
  /** Резолвер user_id → имя (справочник). Fallback — короткий id. */
  resolveUserName?: (userId: string) => string | undefined;
  /**
   * F60 (TPL-F1): что ВОШЛО — первым блоком. Успех отчитывался только об
   * исключениях, и «не вошло: комментариев 6» читалось как частичный провал без
   * ответа, что же создано.
   */
  included?: ReadonlyArray<{ key: string; label: string; value: number }> | undefined;
  /**
   * F66 круг 1 (UX, MAJOR): пропусков нет — сказать это строкой. Пропавший блок
   * «что не вошло» нельзя отличить от незагруженного.
   */
  showNothingSkipped?: boolean;
};

// F4.7 — карта warning-код → i18n (пока один код; неизвестный рендерится как есть).
const WARN_LABEL: Record<string, () => string> = {
  default_state_assigned: m.tmpl_warning_default_state_assigned,
};

export function TemplateOpSummary({
  skipped,
  warnings,
  droppedAssignees,
  resolveUserName,
  included,
  showNothingSkipped,
}: TemplateOpSummaryProps): JSX.Element | null {
  // F60 (TPL-F5): число — в своей колонке, моноширинными табличными цифрами, а не
  // «comments: 6» строкой после двоеточия.
  const items: Array<{ code: string; label: string; count: number }> = [];
  if (skipped) {
    for (const [code, count] of Object.entries(skipped)) {
      if (!count || count <= 0) continue;
      items.push({ code, label: SKIP_LABEL_TEXT[code]?.() ?? code, count });
    }
  }
  const inc = (included ?? []).filter((r) => r.value > 0);
  const dropped = droppedAssignees ?? [];
  const warns = (warnings ?? []).map((w) => WARN_LABEL[w]?.() ?? w);

  const skippedAny = items.length > 0 || dropped.length > 0 || warns.length > 0;
  if (!skippedAny && inc.length === 0) return null;

  return (
    <div className={styles.summary} role="status">
      {inc.length > 0 && (
        <>
          <div className={styles.title}>{m.tmpl_summary_included_title()}</div>
          <dl className={styles.grid}>
            {inc.map((r) => (
              <div key={r.key} className={styles.row}>
                <dt>{r.label}</dt>
                <dd className={styles.num}>{r.value}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
      {skippedAny && (
        <div className={inc.length > 0 ? styles.titleSplit : styles.title}>
          {m.tmpl_summary_title()}
        </div>
      )}
      {!skippedAny && showNothingSkipped && (
        <div className={styles.nothingSkipped}>{m.tmpl_summary_nothing_skipped()}</div>
      )}
      {warns.length > 0 && (
        <ul className={styles.list}>
          {warns.map((w) => (
            <li key={w} className={styles.warn}>
              {w}
            </li>
          ))}
        </ul>
      )}
      {items.length > 0 && (
        <dl className={styles.grid}>
          {items.map((it) => (
            <div key={it.code} className={styles.row}>
              <dt className={styles.item}>{it.label}</dt>
              <dd className={styles.num}>{it.count}</dd>
            </div>
          ))}
        </dl>
      )}
      {dropped.length > 0 && (
        <div className={styles.dropped}>
          <span className={styles.droppedLabel}>
            {m.tmpl_dropped_assignees({ count: dropped.length })}
          </span>
          <span className={styles.droppedNames}>
            {dropped.map((d) => resolveUserName?.(d.user_id) ?? d.user_id.slice(0, 8)).join(', ')}
          </span>
        </div>
      )}
    </div>
  );
}
